import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";

export const MEMORY_PAIRS = 8;
/** How long a mismatched pair stays face up before flipping back. */
export const MEMORY_PEEK_MS = 1400;

const EMOJI_BANK = [
	"🦊",
	"🐸",
	"🐙",
	"🦄",
	"🐧",
	"🦁",
	"🐨",
	"🐝",
	"🦖",
	"🐢",
	"🦋",
	"🐳",
	"🦉",
	"🐼",
] as const;

export interface MemoryCard {
	/** Empty string for a card the viewer has not earned a look at yet. */
	emoji: string;
	owner: PlayerIndex | null;
}

export interface MemoryState extends BaseState {
	kind: "memory";
	cards: MemoryCard[];
	/** Cards the current player has turned over this turn (0, 1 or 2 of them). */
	faceUp: number[];
	/** When a mismatched pair flips back down. */
	hideAt: number | null;
	/** Set for one tick after a match, so the UI can celebrate. */
	lastMatch: number[] | null;
}

export interface MemoryMove {
	card: number;
}

export const memory: GameDefinition<MemoryState, MemoryMove> = {
	id: "memory",
	category: "puzzle",
	title: "Memory Match",
	emoji: "🧠",
	blurb: "Flip two cards. Find a pair and you get another go.",
	howTo: [
		"Turn over two cards on your turn.",
		"If they match you keep the pair and go again.",
		"If they don't, they flip back and it's your friend's turn.",
		"Most pairs when the board is empty wins.",
	],

	create(rng) {
		const faces = rng.sample(EMOJI_BANK, MEMORY_PAIRS);
		const deck = rng.shuffle([...faces, ...faces]);
		return {
			kind: "memory",
			cards: deck.map((emoji) => ({ emoji, owner: null })),
			faceUp: [],
			hideAt: null,
			lastMatch: null,
			// The opener has the tempo, so it is drawn by lot rather than handed
			// to whoever happens to sit in seat 0.
			turn: rng.int(2) as PlayerIndex,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const card = (raw as { card?: unknown }).card;
		if (typeof card !== "number" || !Number.isInteger(card)) return null;
		if (card < 0 || card >= MEMORY_PAIRS * 2) return null;
		return { card };
	},

	apply(state, player, move, now): MoveOutcome<MemoryState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");
		if (state.hideAt !== null) return reject("Wait for the cards to flip back.");

		const card = state.cards[move.card];
		if (!card) return reject("That card isn't on the board.");
		if (card.owner !== null) return reject("That pair has already been won.");
		if (state.faceUp.includes(move.card)) return reject("That card is already face up.");

		const faceUp = [...state.faceUp, move.card];
		if (faceUp.length === 1) {
			return { ok: true, state: { ...state, faceUp, lastMatch: null } };
		}

		const [firstIndex, secondIndex] = faceUp;
		const first = state.cards[firstIndex];
		const second = state.cards[secondIndex];
		if (!first || !second) return reject("That card isn't on the board.");

		if (first.emoji !== second.emoji) {
			// Leave both showing so the other player gets to see them too.
			return {
				ok: true,
				state: { ...state, faceUp, hideAt: now + MEMORY_PEEK_MS },
			};
		}

		const cards = state.cards.map((c, i) =>
			i === firstIndex || i === secondIndex ? { ...c, owner: player } : c,
		);
		const scores: [number, number] = [...state.scores];
		scores[player] += 1;

		const finished = cards.every((c) => c.owner !== null);
		return {
			ok: true,
			state: {
				...state,
				cards,
				scores,
				faceUp: [],
				hideAt: null,
				lastMatch: [firstIndex, secondIndex],
				// A match means another go.
				turn: finished ? null : player,
				result: finished ? resultFromScores(scores) : null,
			},
		};
	},

	legalMoves(state, player) {
		// Frozen while a mismatched pair is on show.
		if (state.result || state.turn !== player || state.hideAt !== null) return [];
		return state.cards
			.map((card, index) =>
				card.owner === null && !state.faceUp.includes(index) ? { card: index } : null,
			)
			.filter((move): move is MemoryMove => move !== null);
	},

	tick(state, now) {
		if (state.hideAt === null || now < state.hideAt) return null;
		return {
			...state,
			faceUp: [],
			hideAt: null,
			lastMatch: null,
			turn: state.turn === null ? null : otherPlayer(state.turn),
		};
	},

	nextTickAt(state) {
		return state.hideAt;
	},

	redact(state) {
		return {
			...state,
			cards: state.cards.map((card, i) =>
				card.owner === null && !state.faceUp.includes(i) ? { ...card, emoji: "" } : card,
			),
		};
	},
};
